import { Controller } from "@hotwired/stimulus"

interface NormalizedPoint {
  x: number
  y: number
  color?: string
  size?: number
}

declare const Turbo: { renderStreamMessage(html: string): void }

export default class PanelCanvasController extends Controller {
  static targets = ["canvas", "submitBtn", "textInput", "drawTab", "textTab", "drawPane", "textPane", "errorMsg"]
  static values = { submitUrl: String }

  declare canvasTarget: HTMLCanvasElement
  declare submitBtnTarget: HTMLButtonElement
  declare textInputTarget: HTMLTextAreaElement
  declare drawTabTarget: HTMLButtonElement
  declare textTabTarget: HTMLButtonElement
  declare drawPaneTarget: HTMLElement
  declare textPaneTarget: HTMLElement
  declare hasErrorMsgTarget: boolean
  declare errorMsgTarget: HTMLElement
  declare submitUrlValue: string

  private ctx!: CanvasRenderingContext2D
  private drawing = false
  private strokes: NormalizedPoint[][] = []
  private currentStroke: NormalizedPoint[] = []
  private color = "#333333"
  private penSize = 5
  private submitted = false
  private isFullscreen = false
  private inputMode: "draw" | "text" = "draw"
  private logicalWidth = 0
  private logicalHeight = 0

  private _onMouseDown!: (e: MouseEvent) => void
  private _onMouseMove!: (e: MouseEvent) => void
  private _onMouseUp!: (e: MouseEvent) => void
  private _onMouseLeave!: (e: MouseEvent) => void
  private _onTouchStart!: (e: TouchEvent) => void
  private _onTouchMove!: (e: TouchEvent) => void
  private _onTouchEnd!: (e: TouchEvent) => void
  private _onKeyDown!: (e: KeyboardEvent) => void

  private _onTimerExpired!: () => void

  connect(): void {
    this.ctx = this.canvasTarget.getContext("2d")!
    this.drawing = false
    this.strokes = []
    this.currentStroke = []
    this.color = "#333333"
    this.penSize = 5
    this.submitted = false
    this.isFullscreen = false
    this.inputMode = "draw"

    this.setupCanvas()
    this.bindEvents()

    this._onTimerExpired = () => this.handleTimerExpired()
    document.addEventListener("panel-timer:expired", this._onTimerExpired)
  }

  disconnect(): void {
    this.unbindEvents()
    document.removeEventListener("panel-timer:expired", this._onTimerExpired)
  }

  private handleTimerExpired(): void {
    if (this.submitted) return
    if (this.drawing) {
      this.endDraw()
    }
    this.autoSubmit()
  }

  private async autoSubmit(): Promise<void> {
    if (this.submitted) return

    const isTextMode = this.inputMode === "text"
    const textValue = isTextMode ? this.textInputTarget.value.trim() : ""
    const hasDrawing = this.strokes.length > 0

    if (isTextMode && textValue === "" && !hasDrawing) return
    if (!isTextMode && !hasDrawing && textValue === "") return

    await this.submit()
  }

  private setupCanvas(): void {
    const canvas = this.canvasTarget

    canvas.style.width = ""
    canvas.style.height = ""

    const rect = canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1

    canvas.style.width = rect.width + "px"
    canvas.style.height = rect.height + "px"

    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    this.ctx.scale(dpr, dpr)

    this.logicalWidth = rect.width
    this.logicalHeight = rect.height
    this.ctx.lineCap = "round"
    this.ctx.lineJoin = "round"
    this.fillBackground()
  }

  private fillBackground(): void {
    this.ctx.fillStyle = "#ffffff"
    this.ctx.fillRect(0, 0, this.logicalWidth, this.logicalHeight)
  }

  private bindEvents(): void {
    const canvas = this.canvasTarget

    this._onMouseDown = this.startDraw.bind(this)
    this._onMouseMove = this.draw.bind(this)
    this._onMouseUp = this.endDraw.bind(this)
    this._onMouseLeave = this.endDraw.bind(this)
    canvas.addEventListener("mousedown", this._onMouseDown)
    canvas.addEventListener("mousemove", this._onMouseMove)
    canvas.addEventListener("mouseup", this._onMouseUp)
    canvas.addEventListener("mouseleave", this._onMouseLeave)

    this._onTouchStart = this.touchStart.bind(this)
    this._onTouchMove = this.touchMove.bind(this)
    this._onTouchEnd = this.endDraw.bind(this)
    canvas.addEventListener("touchstart", this._onTouchStart, { passive: false })
    canvas.addEventListener("touchmove", this._onTouchMove, { passive: false })
    canvas.addEventListener("touchend", this._onTouchEnd)

    this._onKeyDown = this.handleKeyDown.bind(this)
    ;(this.element as HTMLElement).addEventListener("keydown", this._onKeyDown)
  }

  private unbindEvents(): void {
    const canvas = this.canvasTarget
    canvas.removeEventListener("mousedown", this._onMouseDown)
    canvas.removeEventListener("mousemove", this._onMouseMove)
    canvas.removeEventListener("mouseup", this._onMouseUp)
    canvas.removeEventListener("mouseleave", this._onMouseLeave)
    canvas.removeEventListener("touchstart", this._onTouchStart)
    canvas.removeEventListener("touchmove", this._onTouchMove)
    canvas.removeEventListener("touchend", this._onTouchEnd)
    ;(this.element as HTMLElement).removeEventListener("keydown", this._onKeyDown)
  }

  private getPosition(event: { clientX: number; clientY: number }): { x: number; y: number } {
    const rect = this.canvasTarget.getBoundingClientRect()
    const scaleX = this.logicalWidth / rect.width
    const scaleY = this.logicalHeight / rect.height
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY
    }
  }

  private normalize(pos: { x: number; y: number }): NormalizedPoint {
    return { x: pos.x / this.logicalWidth, y: pos.y / this.logicalHeight }
  }

  private denormalize(norm: NormalizedPoint): { x: number; y: number } {
    return { x: norm.x * this.logicalWidth, y: norm.y * this.logicalHeight }
  }

  private startDraw(event: { clientX: number; clientY: number }): void {
    if (this.submitted) return
    this.drawing = true
    const pos = this.getPosition(event)
    const norm = this.normalize(pos)
    this.currentStroke = [{ ...norm, color: this.color, size: this.penSize }]
    this.ctx.beginPath()
    this.ctx.strokeStyle = this.color
    this.ctx.lineWidth = this.penSize
    this.ctx.moveTo(pos.x, pos.y)
  }

  private draw(event: { clientX: number; clientY: number }): void {
    if (!this.drawing || this.submitted) return
    const pos = this.getPosition(event)
    this.currentStroke.push(this.normalize(pos))
    this.ctx.lineTo(pos.x, pos.y)
    this.ctx.stroke()
    this.ctx.beginPath()
    this.ctx.moveTo(pos.x, pos.y)
  }

  private endDraw(): void {
    if (!this.drawing) return
    this.drawing = false
    if (this.currentStroke.length > 0) {
      this.strokes.push(this.currentStroke)
      this.currentStroke = []
    }
  }

  private touchStart(event: TouchEvent): void {
    event.preventDefault()
    const touch = event.touches[0]
    this.startDraw({ clientX: touch.clientX, clientY: touch.clientY })
  }

  private touchMove(event: TouchEvent): void {
    event.preventDefault()
    const touch = event.touches[0]
    this.draw({ clientX: touch.clientX, clientY: touch.clientY })
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.submitted) return

    if ((event.ctrlKey || event.metaKey) && event.key === "z") {
      event.preventDefault()
      this.undo()
      return
    }

    if (event.key === "Delete") {
      event.preventDefault()
      this.clear()
      return
    }
  }

  clear(): void {
    if (this.submitted) return
    this.strokes = []
    this.fillBackground()
  }

  undo(): void {
    if (this.submitted || this.strokes.length === 0) return
    this.strokes.pop()
    this.redraw()
  }

  changeColor(event: Event & { params: { color: string } }): void {
    this.color = event.params.color
    this.element.querySelectorAll(".panel-color-swatch").forEach(s => {
      s.classList.remove("panel-color-swatch--active")
      s.setAttribute("aria-pressed", "false")
    })
    const target = event.currentTarget as HTMLElement
    target.classList.add("panel-color-swatch--active")
    target.setAttribute("aria-pressed", "true")
  }

  changePenSize(event: Event & { params: { size: string } }): void {
    this.penSize = parseInt(event.params.size)
    this.element.querySelectorAll(".btn-pen-size").forEach(b => {
      b.classList.remove("btn-pen-size--active")
      b.setAttribute("aria-pressed", "false")
    })
    const target = event.currentTarget as HTMLElement
    target.classList.add("btn-pen-size--active")
    target.setAttribute("aria-pressed", "true")
  }

  private redraw(): void {
    this.fillBackground()
    for (const stroke of this.strokes) {
      if (stroke.length === 0) continue
      const start = this.denormalize(stroke[0])
      this.ctx.beginPath()
      this.ctx.strokeStyle = stroke[0].color || "#333333"
      this.ctx.lineWidth = stroke[0].size || 5
      this.ctx.moveTo(start.x, start.y)
      for (let i = 1; i < stroke.length; i++) {
        const pt = this.denormalize(stroke[i])
        this.ctx.lineTo(pt.x, pt.y)
        this.ctx.stroke()
        this.ctx.beginPath()
        this.ctx.moveTo(pt.x, pt.y)
      }
    }
  }

  switchTab(event: Event & { params: { mode: string } }): void {
    if (this.submitted) return
    const mode = event.params.mode as "draw" | "text"
    if (mode === this.inputMode) return

    this.inputMode = mode

    this.drawTabTarget.classList.toggle("panel-tab--active", mode === "draw")
    this.textTabTarget.classList.toggle("panel-tab--active", mode === "text")
    this.drawTabTarget.setAttribute("aria-selected", String(mode === "draw"))
    this.textTabTarget.setAttribute("aria-selected", String(mode === "text"))
    this.drawPaneTarget.style.display = mode === "draw" ? "" : "none"
    this.textPaneTarget.style.display = mode === "text" ? "" : "none"

    if (mode === "text" && this.isFullscreen) {
      this.isFullscreen = false
      this.element.classList.remove("panel-canvas-container--fullscreen")
      document.body.style.overflow = ""
    }

    if (mode === "text") {
      this.textInputTarget.focus()
    }
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen
    this.element.classList.toggle("panel-canvas-container--fullscreen", this.isFullscreen)
    document.body.style.overflow = this.isFullscreen ? "hidden" : ""

    setTimeout(() => {
      this.setupCanvas()
      this.redraw()
    }, 50)
  }

  private showSubmitError(): void {
    if (this.hasErrorMsgTarget) {
      this.errorMsgTarget.textContent = this.errorMsgTarget.dataset.errorText || "Submission failed"
      this.errorMsgTarget.style.display = ""
    }
  }

  private hideSubmitError(): void {
    if (this.hasErrorMsgTarget) {
      this.errorMsgTarget.style.display = "none"
    }
  }

  async submit(): Promise<void> {
    if (this.submitted) return

    const isTextMode = this.inputMode === "text"
    const textValue = isTextMode ? this.textInputTarget.value.trim() : ""
    const hasDrawing = this.strokes.length > 0

    if (isTextMode && textValue === "") return
    if (!isTextMode && !hasDrawing) return

    if (this.isFullscreen) {
      this.isFullscreen = false
      this.element.classList.remove("panel-canvas-container--fullscreen")
      document.body.style.overflow = ""
    }

    this.submitted = true
    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.textContent = "..."
    this.hideSubmitError()

    const body: Record<string, string> = {}

    if (!isTextMode && hasDrawing) {
      const submitCanvas = document.createElement("canvas")
      submitCanvas.width = 400
      submitCanvas.height = 300
      const submitCtx = submitCanvas.getContext("2d")!
      submitCtx.drawImage(this.canvasTarget, 0, 0, 400, 300)
      body.image_data = submitCanvas.toDataURL("image/png")
    }

    if (isTextMode && textValue !== "") {
      body.text_data = textValue
    }

    const csrfMeta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement
    const csrfToken = csrfMeta.content

    try {
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify(body)
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else {
        console.warn("[panel-canvas] Submit failed:", response.status, response.statusText)
        this.submitted = false
        this.submitBtnTarget.disabled = false
        this.showSubmitError()
      }
    } catch (error) {
      console.warn("[panel-canvas] Submit error:", error)
      this.submitted = false
      this.submitBtnTarget.disabled = false
      this.showSubmitError()
    }
  }
}
